import { Component } from './component-base.js';

/**
 * StickyHeader
 * Handles sticky header behavior with scroll detection and dynamic show/hide
 */
class StickyHeader extends Component {
  constructor() {
    super();
    
    this.scrollThreshold = 50;
    this.scrollResetThreshold = 12;
    this.scrollTolerance = 5;
    this.lastScrollY = 0;
    this.currentScrollY = 0;
    this.isScrolled = false;
    this.isHidden = false;
    this.isFloatingHeader = false;
    this.headerElement = null;
    this.headerType = 'none';
    this.scrollFrame = null;
    this.resizeFrame = null;
    this.returnToFloatingTimeout = null;
    this.toolbarElement = null;
    this.toolbarHeight = 0;
    this.headerGroupWrapper = null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    this.headerElement = this.closest('header');
    if (!this.headerElement) {
      console.error('[StickyHeader] No parent <header> element found');
      return;
    }

    this.headerGroupWrapper = this.headerElement.closest('.shopify-section-group-header-group');
    this.isFloatingHeader = this.headerElement.dataset.floatingHeader === 'true';

    if (this.isFloatingHeader) {
      this.scrollResetThreshold = 24;
      this.scrollTolerance = 8;
    }

    this.headerType = this.headerElement.dataset.headerType || 'none';

    if (this.headerType === 'none') {
      return;
    }

    this.currentScrollY = window.scrollY;
    this.lastScrollY = this.currentScrollY;

    // Check for sticky toolbar and calculate offset
    this.#calculateToolbarOffset();
    this.#updateFloatingThreshold();

    this.#addEventListeners();

    if (this.currentScrollY <= this.scrollThreshold && this.headerElement.hasAttribute('data-header-hidden')) {
      this.headerElement.removeAttribute('data-header-hidden');
      this.headerElement.style.transition = '';
    }

    this.#updateHeaderState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
    
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
    }

    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
    }

    this.#clearReturnToFloatingTimeout();
  }

  #addEventListeners() {
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize, { passive: true });
    
    // Add hover listeners if remove transparency on hover is enabled
    if (this.headerElement.hasAttribute('data-remove-transparency-on-hover')) {
      this.headerElement.addEventListener('mouseenter', this.#handleMouseEnter);
      this.headerElement.addEventListener('mouseleave', this.#handleMouseLeave);
    }
  }

  #removeEventListeners() {
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);
    
    if (this.headerElement.hasAttribute('data-remove-transparency-on-hover')) {
      this.headerElement.removeEventListener('mouseenter', this.#handleMouseEnter);
      this.headerElement.removeEventListener('mouseleave', this.#handleMouseLeave);
    }
  }

  #handleMouseEnter = () => {
    this.headerElement.classList.add('header-hovered');
  };

  #handleMouseLeave = () => {
    this.headerElement.classList.remove('header-hovered');
  };

  #handleScroll = () => {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      const nextScrollY = window.scrollY;

      if (nextScrollY !== this.currentScrollY) {
        this.currentScrollY = nextScrollY;
        this.#updateHeaderState();
      }

      this.scrollFrame = null;
    });
  };

  #handleResize = () => {
    if (this.resizeFrame) return;

    this.resizeFrame = requestAnimationFrame(() => {
      this.#calculateToolbarOffset();
      this.#updateFloatingThreshold();
      this.#updateHeaderState();
      this.resizeFrame = null;
    });
  };

  #updateHeaderState() {
    const scrollDelta = this.currentScrollY - this.lastScrollY;
    
    if (this.headerElement.hasAttribute('data-header-hidden')) {
      this.headerElement.removeAttribute('data-header-hidden');
      this.headerElement.style.transition = '';
    }
    
    this.#updateScrolledClass();
    
    if (this.headerType === 'scroll-up') {
      this.#updateScrollUpBehavior(scrollDelta);
    }
    
    this.lastScrollY = this.currentScrollY;
  }

  #updateScrolledClass() {
    if (!this.isScrolled) {
      if (this.currentScrollY > this.scrollThreshold) {
        this.#clearReturnToFloatingTimeout();
        this.#setScrolledState(true);
      }

      return;
    }

    if (this.currentScrollY > this.scrollResetThreshold) {
      this.#clearReturnToFloatingTimeout();
      return;
    }

    this.#scheduleReturnToFloating();
  }

  #updateScrollUpBehavior(scrollDelta) {
    if (this.currentScrollY <= this.scrollThreshold) {
      if (this.isHidden) {
        this.#showHeader();
      }
      return;
    }
    
    if (this.headerElement.classList.contains('menu-open')) {
      if (this.isHidden) {
        this.#showHeader();
      }
      return;
    }
    
    if (scrollDelta > this.scrollTolerance) {
      if (!this.isHidden) {
        this.#hideHeader();
      }
    } else if (scrollDelta < -this.scrollTolerance) {
      if (this.isHidden) {
        this.#showHeader();
      }
    }
  }

  #hideHeader() {
    this.isHidden = true;
    this.headerElement.classList.add('header-hidden');
  }

  #showHeader() {
    this.isHidden = false;
    this.headerElement.classList.remove('header-hidden');
  }

  #setScrolledState(nextState) {
    if (nextState === this.isScrolled) {
      return;
    }

    this.isScrolled = nextState;

    if (this.isScrolled) {
      this.headerElement.classList.add('scrolled');
    } else {
      this.headerElement.classList.remove('scrolled');
    }
  }

  #scheduleReturnToFloating() {
    if (this.returnToFloatingTimeout) {
      return;
    }

    this.returnToFloatingTimeout = setTimeout(() => {
      this.returnToFloatingTimeout = null;

      if (this.currentScrollY <= this.scrollResetThreshold && !this.headerElement.classList.contains('menu-open')) {
        this.#setScrolledState(false);
      }
    }, 120);
  }

  #clearReturnToFloatingTimeout() {
    if (!this.returnToFloatingTimeout) {
      return;
    }

    clearTimeout(this.returnToFloatingTimeout);
    this.returnToFloatingTimeout = null;
  }

  #updateFloatingThreshold() {
    if (!this.isFloatingHeader || !this.headerElement) {
      return;
    }

    const headerHeight = Math.ceil(this.headerElement.offsetHeight || 0);

    if (!headerHeight) {
      return;
    }

    this.scrollThreshold = headerHeight;
    this.scrollResetThreshold = Math.max(Math.min(Math.round(headerHeight * 0.2), 24), 12);
    this.scrollTolerance = Math.max(Math.round(headerHeight * 0.08), 8);
  }

  #calculateToolbarOffset() {
    if (!this.headerGroupWrapper) {
      return;
    }

    // Find sticky toolbar (could be above or below header in header group)
    this.toolbarElement = document.querySelector('.toolbar-section');
    if (!this.toolbarElement) {
      this.toolbarHeight = 0;
      this.headerGroupWrapper.style.top = '0px';
      return;
    }


    // Check if toolbar has sticky positioning
    const toolbarStyles = window.getComputedStyle(this.toolbarElement);
    const isSticky = toolbarStyles.position === 'sticky' || toolbarStyles.position === 'fixed';
    
    if (!isSticky) {
      this.toolbarHeight = 0;
      this.headerGroupWrapper.style.top = '0px';
      return;
    }

    // Check if toolbar is above the header
    const toolbarRect = this.toolbarElement.getBoundingClientRect();
    const headerRect = this.headerGroupWrapper.getBoundingClientRect();
    const isToolbarAboveHeader = toolbarRect.top < headerRect.top;
    
    if (isToolbarAboveHeader) {
      // Toolbar is above header, set header top offset
      this.toolbarHeight = this.toolbarElement.offsetHeight;
      this.headerGroupWrapper.style.top = `${this.toolbarHeight}px`;
    } else {
      // Toolbar is below header, no offset needed
      this.toolbarHeight = 0;
      this.headerGroupWrapper.style.top = '0px';
    }
  }
}

function initStickyHeader() {
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', StickyHeader);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyHeader);
} else {
  initStickyHeader();
}

export { StickyHeader, initStickyHeader };
